import { useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const DB_NAME    = 'sitescope-offline';
const STORE_NAME = 'track-queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = e => {
      e.target.result.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = e => resolve(e.target.result);
    req.onerror   = e => reject(e.target.error);
  });
}

// Every queued item records the userId it belongs to so replay can only ever
// apply an action to the account that created it — never to whoever happens to be
// signed in when the browser next comes online (WP-19).
export async function enqueueTrackAction({ action, daId, userId }) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add({ action, daId, userId: userId || null, timestamp: Date.now() });
    tx.oncomplete = resolve;
    tx.onerror    = e => reject(e.target.error);
  });
}

async function getAllQueued() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = e => resolve(e.target.result);
    req.onerror   = e => reject(e.target.error);
  });
}

async function deleteQueued(ids) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    ids.forEach(id => store.delete(id));
    tx.oncomplete = resolve;
    tx.onerror    = e => reject(e.target.error);
  });
}

// Partition queued items for the signed-in user. Pure + exported so the
// user-isolation policy can be unit-tested without IndexedDB:
//   - replay:      items belonging to the current user (safe to apply)
//   - purgeIds:    legacy items with no userId (unattributable → delete)
//   - quarantined: items belonging to a different user (keep for that user)
export function classifyQueueItems(items, userId) {
  const replay = [];
  const purgeIds = [];
  const quarantined = [];
  for (const item of (items || [])) {
    if (!item.userId) { purgeIds.push(item.id); continue; }
    if (item.userId !== userId) { quarantined.push(item.id); continue; }
    replay.push(item);
  }
  return { replay, purgeIds, quarantined };
}

// Call this hook once at the app root level to auto-sync when coming back online.
export function useOfflineSync(session) {
  const userId = session?.user?.id || null;

  const syncQueue = useCallback(async () => {
    if (!userId) return;
    const items = await getAllQueued();
    if (!items.length) return;

    const { replay, purgeIds, quarantined } = classifyQueueItems(items, userId);
    const syncedIds = [];   // successfully replayed for this user → delete

    for (const item of replay) {
      try {
        if (item.action === 'track') {
          const { error } = await supabase
            .from('tracked_das')
            .upsert(
              { user_id: userId, da_id: item.daId },
              { onConflict: 'user_id,da_id', ignoreDuplicates: true }
            );
          if (error) throw error;
        } else if (item.action === 'untrack') {
          const { error } = await supabase.from('tracked_das').delete().eq('user_id', userId).eq('da_id', item.daId);
          if (error) throw error;
        } else {
          continue;
        }
        syncedIds.push(item.id);
      } catch {
        // Keep failed queue items so they can retry next time the browser is online.
      }
    }

    if (purgeIds.length > 0) {
      console.warn(`[useOfflineSync] purged ${purgeIds.length} unattributable (pre-userId) offline queue item(s)`);
    }
    if (quarantined.length > 0) {
      console.warn(`[useOfflineSync] quarantined ${quarantined.length} offline queue item(s) belonging to another user`);
    }
    const toDelete = [...syncedIds, ...purgeIds];
    if (toDelete.length > 0) await deleteQueued(toDelete);
  // Keyed to the user id (not the session object) so token refreshes don't
  // re-register the online listener with a new callback identity.
  }, [userId]);

  useEffect(() => {
    // Attempt sync on mount when already online — a queue built offline is
    // otherwise never replayed if the app next opens already-online (the
    // `online` event only fires on an offline→online transition).
    if (typeof navigator === 'undefined' || navigator.onLine) syncQueue();
    window.addEventListener('online', syncQueue);
    return () => window.removeEventListener('online', syncQueue);
  }, [syncQueue]);
}
